'use client';

import { useTranslation } from '@/app/i18n/client';
import { GoogleReCaptchaV2 } from '@/components/GoogleReCaptchaV2';
import MaspLogo from '@/components/icons/maspLogo';
import { LoginDialog } from '@/components/loginDialog';
import { MaskInput } from '@/components/maskInput';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useToast } from '@/components/ui/use-toast';
import { useAuthV2 } from '@/hooks/useAuthV2';
import MASP from '@/public/static/MASP.webp';
import { COUNTRIES } from '@/utils/constants';
import { formatRetryTime } from '@/utils/date';
import { setPendingAuth } from '@/utils/pending-auth';
import { setSessionStorageWithoutParse } from '@/utils/session-storage';
import { AuthMethod } from '@/utils/types';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Mail, Smartphone } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useParams, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox='0 0 24 24' fill='currentColor' xmlns='http://www.w3.org/2000/svg'>
    <path d='M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z' />
  </svg>
);

interface LoginFormInput {
  phone: string;
  email: string;
}

function Login() {
  const { lng } = useParams() as { lng: 'pt-BR' | 'es' | 'en' };
  const { t } = useTranslation(lng as string, ['login', 'errors']);
  const { handleSubmit, watch, control, setValue } = useForm<LoginFormInput>({
    defaultValues: { phone: '', email: '' },
  });
  const [sendingCode, setSendingCode] = useState(false);
  const [open, setOpen] = useState<boolean>(false);
  const [openPinDialog, setOpenPinDialog] = useState<boolean>(false);
  const [isNewUserLogin, setIsNewUserLogin] = useState(false);
  const [authMethod, setAuthMethod] = useState<AuthMethod>('whatsapp');
  const [whatsappErrorCount, setWhatsappErrorCount] = useState(0);
  const [shouldEnableSMS, setShouldEnableSMS] = useState(false);
  const [isDependentLogin, setIsDependentLogin] = useState(false);
  const [dependentCodeValue, setDependentCodeValue] = useState('');
  const [sentToEmail, setSentToEmail] = useState('');
  const {
    handleSignIn,
    sendWhatsAppCode,
    sendEmailVerificationCode,
    sendDependentCode,
    isValidEmail,
    user,
    userLoaded,
    isRateLimitError,
    isSMSDefenseError,
    isForeignDDI,
  } = useAuthV2();
  const { authenticateUsers, loginWithEmail, whatsappLogin, smsLogin = true } = useFlags();

  const [hideCaptcha, setHideCaptcha] = useState<boolean>(false);
  const router = useRouter();
  const { showToast } = useToast();
  const searchParams = useSearchParams();
  const redirectURI = searchParams?.get('redirectURI') || null;

  // Function to validate if redirectURI is an internal page
  const isValidInternalRedirect = (uri: string | null): boolean => {
    if (!uri) return true; // No redirect is valid (will use default)

    try {
      const url = new URL(uri);
      // Only allow redirects to the same origin (internal pages)
      return url.origin === (typeof window !== 'undefined' ? window.location.origin : '');
    } catch {
      // If it's not a valid URL, treat it as a relative path (internal)
      return uri.startsWith('/');
    }
  };

  const validRedirectURI = isValidInternalRedirect(redirectURI) ? redirectURI : null;

  useEffect(() => {
    // Só redireciona quando o estado está completamente carregado
    // Isso evita race conditions em dispositivos móveis
    if (userLoaded && user) {
      if (validRedirectURI) {
        router.push(validRedirectURI);
      } else {
        router.push('/my-account');
      }
    }
  }, [user, userLoaded, validRedirectURI, router]);

  const phone = watch('phone');
  const email = watch('email');

  // Reset error count when phone number changes
  useEffect(() => {
    setWhatsappErrorCount(0);
    setShouldEnableSMS(false);
  }, [phone]);

  // Reset error count when auth method changes
  useEffect(() => {
    if (authMethod !== 'whatsapp') {
      setWhatsappErrorCount(0);
    }
  }, [authMethod]);

  // Reset error count when modal opens (success)
  useEffect(() => {
    if (openPinDialog) {
      setWhatsappErrorCount(0);
      setShouldEnableSMS(false);
    } else {
      setIsNewUserLogin(false);
    }
  }, [openPinDialog]);

  const hasMultipleMethods =
    (smsLogin && shouldEnableSMS ? 1 : 0) + (loginWithEmail ? 1 : 0) + (whatsappLogin ? 1 : 0) > 1;

  const switchAuthMethod = (method: AuthMethod) => {
    if (method === 'sms' && (!smsLogin || !shouldEnableSMS)) return;
    if (method === 'email' && !loginWithEmail) return;
    if (method === 'whatsapp' && !whatsappLogin) return;
    setAuthMethod(method);
  };

  // Set initial auth method based on available flags
  // Only force change if SMS is not enabled via error count
  useEffect(() => {
    if (authMethod === 'sms' && !shouldEnableSMS && !smsLogin) {
      if (whatsappLogin) setAuthMethod('whatsapp');
      else if (loginWithEmail) setAuthMethod('email');
    }
  }, [whatsappLogin, loginWithEmail, authMethod, shouldEnableSMS, smsLogin]);

  const clearPhone = (phone: string) => {
    return phone.replace(/\D/g, '');
  };

  const brazilIndex = COUNTRIES.findIndex((country) =>
    ['Brazil', 'Brasil'].includes(country.name[(lng as 'pt-BR' | 'es' | 'en') || 'pt-BR'])
  );
  const [selectedCountry, setSelectedCountry] = useState(brazilIndex);

  const DDI = COUNTRIES[selectedCountry]?.mobileCode;
  const COUNTRY: string = COUNTRIES[selectedCountry]?.name[(lng as 'pt-BR' | 'es' | 'en') || 'pt-BR'];

  const formattedPhone = useMemo(() => `${DDI}${clearPhone(phone || '')}`, [phone, DDI]);

  const confirm = async () => {
    setSendingCode(true);
    try {
      if (authMethod === 'sms') {
        const response = await handleSignIn(formattedPhone);
        if (response?.user?.id) {
          setOpenPinDialog(true);
          setHideCaptcha(true);

          const sessionValue = response?.session || '';
          if (sessionValue) {
            setSessionStorageWithoutParse('mfa_session', sessionValue);
          }
          setPendingAuth({ method: 'sms', identifier: `+${formattedPhone}`, session: sessionValue });
        } else {
          router.push(validRedirectURI || '/');
        }
      } else if (authMethod === 'whatsapp') {
        const response = await sendWhatsAppCode(formattedPhone);
        if (response) {
          setOpenPinDialog(true);
          setHideCaptcha(true);
          // Reset error count on success
          setWhatsappErrorCount(0);
          setShouldEnableSMS(false);
          setPendingAuth({ method: 'whatsapp', identifier: `+${formattedPhone}`, session: '' });
        }
      } else if (authMethod === 'email') {
        if (!isValidEmail(email)) {
          showToast({ title: t('invalidEmail'), description: t('pleaseEnterValidEmail') });
          setSendingCode(false);
          return;
        }
        const response = await sendEmailVerificationCode(email);
        if (response.success) {
          setIsNewUserLogin(!!response.isNewUser);
          setOpenPinDialog(true);
          setHideCaptcha(true);
          if (!response.isNewUser) {
            setPendingAuth({ method: 'email', identifier: email, session: '' });
          }
        } else {
          showToast({ title: t('emailSendFailed'), description: response.message });
        }
      } else if (authMethod === 'dependent_code') {
        if (!dependentCodeValue.trim()) {
          showToast({ title: t('dependentCodeInvalid'), description: t('dependentCodePlaceholder') });
          setSendingCode(false);
          return;
        }
        const response = await sendDependentCode(dependentCodeValue.trim());
        if (response?.sentToEmail) {
          setSentToEmail(response.sentToEmail);
          setOpenPinDialog(true);
          setHideCaptcha(true);
          setPendingAuth({ method: 'dependent_code', identifier: dependentCodeValue.trim(), session: '' });
        }
      }
    } catch (error: any) {
      // Count WhatsApp errors that don't open the modal
      let newErrorCount = whatsappErrorCount;
      if (authMethod === 'whatsapp') {
        newErrorCount = whatsappErrorCount + 1;
        setWhatsappErrorCount(newErrorCount);

        // Enable SMS after 5 consecutive errors
        if (newErrorCount >= 5) {
          setShouldEnableSMS(true);
        }
      }

      const retryAfter = error?.retryAfter || error?.payload?.remainingSeconds || error?.remainingSeconds;
      const errorCode = error?.code;
      const retrySeconds = retryAfter || 60;
      const retryTime = formatRetryTime(retrySeconds, lng as string);

      let title = '';
      let description = '';

      if (isSMSDefenseError(errorCode)) {
        const smsTitle = t('errors:smsUnavailable');
        let smsDescription = t(`errors:${errorCode}`) || t('errors:anErrorOccurred');

        if (loginWithEmail) {
          smsDescription += ` ${t('errors:suggestionUseEmail')}`;
          setTimeout(() => switchAuthMethod('email'), 1500);
        }

        showToast({
          title: smsTitle,
          description: smsDescription,
          variant: 'warning',
          duration: 7000,
        });
        return;
      }

      if (isRateLimitError(errorCode) || errorCode === 400668 || errorCode === 400669 || errorCode === 400724) {
        title = t('errors:tooManyAttempts');

        if (errorCode === 400806) {
          description = t('errors:400806', { retryTime });
        } else if (errorCode === 400668) {
          description = t('errors:400668', { remainingSeconds: retrySeconds });
        } else if ([400669, 400724].includes(errorCode)) {
          description = t(`errors:${errorCode}`, { retryTime });
        }
      } else {
        title = t(`errors:${errorCode}`) || t('errors:errorTitle');
        description = t('errors:errorSigningInDescription');
      }

      // Add SMS suggestion message if SMS was enabled due to errors
      if (authMethod === 'whatsapp' && newErrorCount >= 5) {
        const smsMessage = t('errors:trySmsInstead') || 'Tente usar SMS como alternativa.';
        description = description ? `${description} ${smsMessage}` : smsMessage;
      }

      showToast({
        title: title || t('errors:errorTitle'),
        description: description || t('errors:anErrorOccurred'),
        variant: 'error',
        duration: 8000,
      });
    } finally {
      setSendingCode(false);
    }
  };

  return (
    <>
      <div className='relative flex h-screen w-screen max-[1024px]:-bottom-2 max-[1024px]:-right-2 max-[1024px]:block'>
        <div className='absolute left-4 top-6 flex w-full items-center lg:left-6 lg:top-8'>
          <Link href={'/'}>
            <MaspLogo size={40} />
          </Link>
        </div>

        <div className='max-[1024px]:mb-6 max-[1024px]:h-96 max-[1024px]:w-full lg:basis-1/2'>
          <Image src={MASP} alt='MASP' width='1920' height='1080' className='h-full w-full object-cover' />
        </div>

        <div className='relative flex flex-1 items-center justify-center overflow-hidden lg:basis-1/2'>
          <div className='max-w-md p-4'>
            <h1 className='mb-5 text-[50px] font-extrabold'>{t('login')}</h1>

            <p className='mb-10 text-base text-foreground'>
              {isDependentLogin ? t('dependentCodeSubtitle') : t('reviewInfo')}
            </p>

            <form className='flex flex-col gap-5' onSubmit={handleSubmit(confirm)}>
              {/* Auth method selector */}
              {hasMultipleMethods && !isDependentLogin && (
                <div className='mb-4 flex items-center justify-center gap-2'>
                  {smsLogin && shouldEnableSMS && (
                    <Button
                      type='button'
                      variant={authMethod === 'sms' ? 'default' : 'outline'}
                      onClick={() => switchAuthMethod('sms')}
                      className='h-12 flex-1 gap-2'
                      disabled={sendingCode}>
                      <Smartphone className='h-4 w-4' />
                      {t('smsAuth')}
                    </Button>
                  )}
                  {whatsappLogin && (
                    <Button
                      type='button'
                      variant={authMethod === 'whatsapp' ? 'default' : 'outline'}
                      onClick={() => switchAuthMethod('whatsapp')}
                      className='h-12 flex-1 gap-2'
                      disabled={sendingCode}>
                      <WhatsAppIcon className='h-4 w-4' />
                      {t('whatsappAuth')}
                    </Button>
                  )}
                  {loginWithEmail && (
                    <Button
                      type='button'
                      variant={authMethod === 'email' ? 'default' : 'outline'}
                      onClick={() => switchAuthMethod('email')}
                      className='h-12 flex-1 gap-2'
                      disabled={sendingCode}>
                      <Mail className='h-4 w-4' />
                      {t('emailAuth')}
                    </Button>
                  )}
                </div>
              )}

              {isDependentLogin ? (
                <div className='flex flex-col items-center justify-center gap-4'>
                  <div className='flex w-full flex-col gap-2'>
                    <span className='inline-flex w-full text-left text-black'>{t('dependentCodeLabel')}</span>
                    <Input
                      autoComplete='off'
                      placeholder={t('dependentCodePlaceholder')}
                      type='text'
                      className='border-0 border-b border-black text-lg text-gray2'
                      value={dependentCodeValue}
                      onChange={(e) => setDependentCodeValue(e.target.value)}
                    />
                  </div>
                </div>
              ) : authMethod === 'email' ? (
                <div className='flex flex-col items-center justify-center gap-4'>
                  <div className='flex w-full flex-col gap-2'>
                    <span className='inline-flex w-full text-left text-foreground'>{t('email')}</span>

                    <Controller
                      control={control}
                      name='email'
                      render={({ field }) => (
                        <Input
                          autoComplete='email'
                          placeholder={t('emailPlaceholder')}
                          type='email'
                          className='border-0 border-b border-black text-lg text-gray2'
                          {...field}
                        />
                      )}
                    />

                    {email && !isValidEmail(email) && (
                      <p className='mt-1 text-sm text-red-600'>{t('pleaseEnterValidEmail')}</p>
                    )}
                  </div>
                </div>
              ) : authMethod === 'sms' ? (
                <div className='flex flex-col items-center justify-center gap-4'>
                  <div className='flex w-full min-w-[150px] flex-col items-center gap-2'>
                    <span className='w-full text-foreground'>{t('country')}</span>
                    <Popover open={open} onOpenChange={setOpen}>
                      <PopoverTrigger asChild>
                        <div className='w-full'>
                          <Button
                            type='button'
                            variant='outline'
                            className='h-11 w-full max-w-full justify-start border-l-0 border-r-0 border-t-0 px-0 text-left text-lg font-medium text-gray2 hover:border-black hover:bg-transparent hover:text-gray2 max-sm:text-base'>
                            {`${COUNTRY} ${DDI}`}
                          </Button>
                        </div>
                      </PopoverTrigger>

                      <PopoverContent className='w-full p-0' align='start' side='bottom'>
                        <Command value={String(selectedCountry)}>
                          <CommandInput placeholder={t('search')} />

                          <CommandList>
                            <CommandEmpty>{t('notFound')}</CommandEmpty>
                            <CommandGroup>
                              {COUNTRIES.map(({ mobileCode, name }) => {
                                if (mobileCode.length !== 1) {
                                  return (
                                    <CommandItem
                                      className='cursor-pointer'
                                      key={name[lng as 'pt-BR' | 'es' | 'en']}
                                      value={name[lng]}
                                      onSelect={(value: string) => {
                                        setSelectedCountry(
                                          COUNTRIES.findIndex(
                                            ({ name }) => (name[lng] as string).toLowerCase() === value.toLowerCase()
                                          )
                                        );
                                        setOpen(false);
                                      }}>
                                      {name[lng]} {mobileCode}
                                    </CommandItem>
                                  );
                                }
                              })}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className='mt-4 flex w-full flex-col gap-2'>
                    <span className='inline-flex w-full text-left text-foreground'>{t('phone')}</span>

                    <Controller
                      control={control}
                      name='phone'
                      render={({ field }) => {
                        if (DDI === '+55') {
                          return (
                            <MaskInput
                              className='border-0 border-b border-black text-lg'
                              autoComplete='off'
                              placeholder={`(DDD) _____-____`}
                              mask={`(99) 99999-9999`}
                              type='tel'
                              {...field}
                            />
                          );
                        }

                        return (
                          <Input
                            autoComplete='off'
                            placeholder={`(DDD) _____-____`}
                            type='tel'
                            className='border-0 border-b border-black text-lg text-gray2'
                            value={field.value}
                            onChange={(event) => {
                              if (!Number(event.target.value) || event.target.value === '') {
                                return field.onChange('');
                              }

                              return field.onChange(event.target.value);
                            }}
                          />
                        );
                      }}
                    />
                    {isForeignDDI(DDI) && loginWithEmail && (
                      <p className='mt-1 text-sm text-amber-600'>{t('foreignPhoneUseEmail')}</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className='flex flex-col items-center justify-center gap-4'>
                  <div className='flex w-full min-w-[150px] flex-col items-center gap-2'>
                    <span className='w-full text-foreground'>{t('country')}</span>
                    <Popover open={open} onOpenChange={setOpen}>
                      <PopoverTrigger asChild>
                        <div className='w-full'>
                          <Button
                            type='button'
                            variant='outline'
                            className='h-11 w-full max-w-full justify-start border-l-0 border-r-0 border-t-0 px-0 text-left text-lg font-medium text-gray2 hover:border-black hover:bg-transparent hover:text-gray2 max-sm:text-base'>
                            {`${COUNTRY} ${DDI}`}
                          </Button>
                        </div>
                      </PopoverTrigger>

                      <PopoverContent className='w-full p-0' align='start' side='bottom'>
                        <Command value={String(selectedCountry)}>
                          <CommandInput placeholder={t('search')} />

                          <CommandList>
                            <CommandEmpty>{t('notFound')}</CommandEmpty>
                            <CommandGroup>
                              {COUNTRIES.map(({ mobileCode, name }) => {
                                if (mobileCode.length !== 1) {
                                  return (
                                    <CommandItem
                                      className='cursor-pointer'
                                      key={name[lng as 'pt-BR' | 'es' | 'en']}
                                      value={name[lng]}
                                      onSelect={(value: string) => {
                                        setSelectedCountry(
                                          COUNTRIES.findIndex(
                                            ({ name }) => (name[lng] as string).toLowerCase() === value.toLowerCase()
                                          )
                                        );
                                        setOpen(false);
                                      }}>
                                      {name[lng]} {mobileCode}
                                    </CommandItem>
                                  );
                                }
                              })}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className='mt-4 flex w-full flex-col gap-2'>
                    <span className='inline-flex w-full text-left text-foreground'>{t('phone')}</span>

                    <Controller
                      control={control}
                      name='phone'
                      render={({ field }) => {
                        if (DDI === '+55') {
                          return (
                            <MaskInput
                              className='border-0 border-b border-black text-lg'
                              autoComplete='off'
                              placeholder={`(DDD) _____-____`}
                              mask={`(99) 99999-9999`}
                              type='tel'
                              {...field}
                            />
                          );
                        }

                        return (
                          <Input
                            autoComplete='off'
                            placeholder={`(DDD) _____-____`}
                            type='tel'
                            className='border-0 border-b border-black text-lg text-gray2'
                            value={field.value}
                            onChange={(event) => {
                              if (!Number(event.target.value) || event.target.value === '') {
                                return field.onChange('');
                              }

                              return field.onChange(event.target.value);
                            }}
                          />
                        );
                      }}
                    />
                    {isForeignDDI(DDI) && loginWithEmail && (
                      <p className='mt-1 text-sm text-amber-600'>{t('foreignPhoneUseEmail')}</p>
                    )}
                  </div>
                </div>
              )}

              {authenticateUsers === 'v2' && !hideCaptcha && !isDependentLogin && (
                <div className='mt-4'>
                  <GoogleReCaptchaV2 action='LOGIN' />
                </div>
              )}

              <Button
                className='mt-4 w-full p-4'
                variant='outline'
                type='submit'
                disabled={
                  isDependentLogin
                    ? !dependentCodeValue.trim()
                    : authMethod === 'email'
                      ? !email || !isValidEmail(email)
                      : authMethod === 'sms'
                        ? !phone || phone.includes('_')
                        : !phone || phone.includes('_')
                }
                isLoading={sendingCode}>
                {t('loginButton')}
              </Button>

              {isDependentLogin ? (
                <button
                  type='button'
                  className='mt-2 text-center text-sm text-gray2 underline hover:text-black'
                  onClick={() => {
                    setIsDependentLogin(false);
                    setAuthMethod('whatsapp');
                    setDependentCodeValue('');
                  }}>
                  {t('backToNormalLogin')}
                </button>
              ) : (
                <button
                  type='button'
                  className='mt-2 text-center text-sm text-gray2 underline hover:text-black'
                  onClick={() => {
                    setIsDependentLogin(true);
                    setAuthMethod('dependent_code');
                  }}>
                  {t('dependentLogin')}
                </button>
              )}
            </form>
          </div>
        </div>
      </div>

      <LoginDialog
        isMFALogin={false}
        phoneNumber={authMethod !== 'email' && authMethod !== 'dependent_code' ? phone : ''}
        emailAddress={authMethod === 'email' ? email : ''}
        defaultPinStep={authMethod !== 'email' || !isNewUserLogin}
        defaultAuthMethod={authMethod === 'sms' ? 'sms' : authMethod === 'email' ? 'email' : 'whatsapp'}
        loginOpen={openPinDialog}
        setLoginOpen={setOpenPinDialog}
        redirectAfterSignIn={validRedirectURI || '/'}
        isNewUserFromPage={authMethod === 'email' && isNewUserLogin}
        dependentCode={isDependentLogin ? dependentCodeValue : undefined}
        sentToEmail={isDependentLogin ? sentToEmail : undefined}
      />
    </>
  );
}

export default Login;
